import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Cookie, ChevronDown, ChevronUp, X, Check, ExternalLink, Settings } from "lucide-react";
import useCookieConsent from "../hooks/useCookieConsent";

const COOKIE_DETAILS = [
  {
    id: "necessary",
    title: "Notwendig",
    description: "Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.",
    always: true,
    cookies: [
      { name: "hoffmann_cookie_consent", provider: "Hoffmann Haustechnik", purpose: "Speichert Ihre Cookie-Einstellungen", expiry: "1 Jahr" },
      { name: "hoffmann_content_cache", provider: "Hoffmann Haustechnik", purpose: "Zwischenspeicherung von Websiteinhalten (Session Storage)", expiry: "Session" },
    ],
  },
  {
    id: "statistics",
    title: "Externe Dienste",
    description: "Diese Kategorie umfasst externe Dienste wie Google Maps. Bei Aktivierung kann Ihre IP-Adresse an den jeweiligen Anbieter übermittelt werden.",
    always: false,
    cookies: [
      { name: "Google Maps", provider: "Google Ireland Ltd.", purpose: "Kartenanzeige auf der Kontaktseite", expiry: "Sitzung" },
    ],
  },
  {
    id: "marketing",
    title: "Marketing",
    description: "Marketing-Cookies werden verwendet, um Besucher über Websites hinweg zu verfolgen. Derzeit setzen wir keine Marketing-Cookies ein.",
    always: false,
    cookies: [
      { name: "Keine", provider: "–", purpose: "Derzeit nicht in Verwendung", expiry: "–" },
    ],
  },
];

const CookieBanner = () => {
  const { showBanner, showSettings, setShowSettings, acceptAll, rejectAll, saveConsent, consent, revokeConsent } = useCookieConsent();
  const [preferences, setPreferences] = useState({ statistics: false, marketing: false });
  const [expandedCategory, setExpandedCategory] = useState(null);

  const handleSaveSelection = () => {
    saveConsent({ necessary: true, ...preferences });
  };

  const toggleCategory = (id) => {
    setExpandedCategory(expandedCategory === id ? null : id);
  };

  if (!showBanner && !showSettings && consent) {
    return (
      <button
        onClick={() => setShowSettings(true)}
        className="fixed bottom-5 left-5 z-[9998] w-11 h-11 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:shadow-xl hover:scale-110 transition-all group"
        title="Cookie-Einstellungen"
      >
        <Cookie size={18} className="text-[#63b32e] group-hover:text-[#2D8C3C]" />
      </button>
    );
  }

  return (
    <AnimatePresence>
      {showBanner && !showSettings && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
        >
          <div className="max-w-[1100px] mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden" role="dialog" aria-label="Cookie-Einstellungen" aria-modal="false">
            <div className="p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#63b32e]/10 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
                  <Shield size={22} className="text-[#63b32e]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-[#1A2332] mb-2">Wir respektieren Ihre Privatsphäre</h3>
                  <p className="text-[#666] text-sm leading-relaxed mb-5">
                    Wir verwenden Cookies und ähnliche Technologien, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten.
                    Einige sind notwendig, andere helfen uns, unsere Website zu verbessern.{" "}
                    <a href="/datenschutz" className="text-[#63b32e] underline underline-offset-2 hover:text-[#2D8C3C]">Mehr erfahren</a>
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <button onClick={acceptAll} className="px-6 py-2.5 bg-[#63b32e] text-white text-sm font-semibold rounded-full hover:bg-[#2D8C3C] hover:shadow-lg transition-all flex items-center gap-2">
                      <Check size={16} /> Alle akzeptieren
                    </button>
                    <button onClick={rejectAll} className="px-6 py-2.5 bg-gray-100 text-[#333] text-sm font-semibold rounded-full hover:bg-gray-200 transition-all">
                      Nur Notwendige
                    </button>
                    <button onClick={() => setShowSettings(true)} className="px-6 py-2.5 text-[#63b32e] text-sm font-semibold rounded-full border border-[#63b32e]/30 hover:bg-[#63b32e]/5 transition-all flex items-center gap-2">
                      <Settings size={14} /> Einstellungen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {showSettings && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget && consent) setShowSettings(false); }}
          role="dialog"
          aria-label="Cookie-Einstellungen"
          aria-modal="true"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-2xl shadow-2xl max-w-[640px] w-full max-h-[85vh] overflow-hidden flex flex-col"
          >
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#63b32e]/10 rounded-xl flex items-center justify-center">
                  <Shield size={20} className="text-[#63b32e]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#1A2332]">Cookie-Einstellungen</h2>
                  <p className="text-xs text-[#888]">Verwalten Sie Ihre Datenschutzpräferenzen</p>
                </div>
              </div>
              {consent && (
                <button onClick={() => setShowSettings(false)} className="p-2 text-[#999] hover:text-[#333] hover:bg-gray-100 rounded-lg transition-colors">
                  <X size={20} />
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <p className="text-[#666] text-sm leading-relaxed">
                Hier können Sie festlegen, welche Cookies Sie zulassen möchten. Notwendige Cookies sind immer aktiv,
                da sie für die Grundfunktionen der Website erforderlich sind.
              </p>

              {COOKIE_DETAILS.map((category) => (
                <div key={category.id} className="border border-gray-100 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => toggleCategory(category.id)}>
                    <div className="flex items-center gap-3">
                      {expandedCategory === category.id ? <ChevronUp size={16} className="text-[#999]" /> : <ChevronDown size={16} className="text-[#999]" />}
                      <div>
                        <span className="font-semibold text-[#1A2332] text-sm">{category.title}</span>
                        {category.always && <span className="ml-2 text-xs text-[#63b32e] bg-[#63b32e]/10 px-2 py-0.5 rounded-full">Immer aktiv</span>}
                      </div>
                    </div>
                    {category.always ? (
                      <div className="w-10 h-5 bg-[#63b32e] rounded-full flex items-center justify-end px-0.5">
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setPreferences((p) => ({ ...p, [category.id]: !p[category.id] })); }}
                        className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${preferences[category.id] ? "bg-[#63b32e] justify-end" : "bg-gray-300 justify-start"}`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full shadow-sm" />
                      </button>
                    )}
                  </div>

                  {expandedCategory === category.id && (
                    <div className="border-t border-gray-100 p-4 bg-[#FAFBFC]">
                      <p className="text-[#666] text-xs mb-3">{category.description}</p>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[#999] border-b border-gray-200">
                            <th className="text-left py-2 font-medium">Cookie</th>
                            <th className="text-left py-2 font-medium">Anbieter</th>
                            <th className="text-left py-2 font-medium">Zweck</th>
                            <th className="text-left py-2 font-medium">Laufzeit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {category.cookies.map((cookie) => (
                            <tr key={cookie.name} className="border-b border-gray-100 last:border-0">
                              <td className="py-2 font-mono text-[#1A2332]">{cookie.name}</td>
                              <td className="py-2 text-[#666]">{cookie.provider}</td>
                              <td className="py-2 text-[#666]">{cookie.purpose}</td>
                              <td className="py-2 text-[#666]">{cookie.expiry}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="p-6 border-t border-gray-100 bg-[#FAFBFC]">
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={handleSaveSelection} className="px-6 py-2.5 bg-[#63b32e] text-white text-sm font-semibold rounded-full hover:bg-[#2D8C3C] transition-all flex items-center gap-2">
                  <Check size={16} /> Auswahl speichern
                </button>
                <button onClick={acceptAll} className="px-6 py-2.5 bg-gray-100 text-[#333] text-sm font-semibold rounded-full hover:bg-gray-200 transition-all">
                  Alle akzeptieren
                </button>
                <button onClick={rejectAll} className="px-6 py-2.5 text-[#666] text-sm font-medium rounded-full hover:bg-gray-100 transition-all">
                  Nur Notwendige
                </button>
                {consent && (
                  <button onClick={revokeConsent} className="ml-auto text-xs text-[#999] hover:text-red-500 transition-colors">
                    Einwilligung widerrufen
                  </button>
                )}
              </div>
              <div className="mt-4 flex items-center gap-4 text-xs text-[#999]">
                <a href="/datenschutz" className="hover:text-[#63b32e] transition-colors flex items-center gap-1">
                  <ExternalLink size={10} /> Datenschutzerklärung
                </a>
                <a href="/impressum" className="hover:text-[#63b32e] transition-colors flex items-center gap-1">
                  <ExternalLink size={10} /> Impressum
                </a>
                {consent?.timestamp && (
                  <span className="ml-auto">Einwilligung erteilt: {new Date(consent.timestamp).toLocaleDateString("de-DE")}</span>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
